import { useEffect, useState } from "react";

import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import PaginationFooter from "common/components/PaginationFooter";
import RenderIf from "common/components/RenderIf";
import usePagination from "common/hooks/usePagination";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LinkedInProfile, SelectedFacetParam } from "common/types";

import useExcludedListLeads from "../../../../datahooks/useExcludedListLeads";
import useListLeadsParams from "../../../../hooks/useListLeadsParams";
import ManageListLeads from "../ManageListLeads";
import NoLeadsPlaceholder from "../NoLeadsPlaceholder";
import NoResultsPlaceholder from "../NoResultsPlaceholder";
import DesktopTable from "./DesktopTable";
import MobileList from "./MobileList";

import type { JSX } from "react";
import type { ListLeadsSearchParams } from "../../../../types";

interface ExcludedLeadsProps {
  listId: string;
  searchParams: ListLeadsSearchParams<SelectedFacetParam>;
  setSearchParams: (
    searchParams: ListLeadsSearchParams<SelectedFacetParam>,
  ) => void;
  //the search input LeadListLeads owns, the same for both tabs
  searchComponent: JSX.Element;
}

export default function ExcludedLeads({
  listId,
  searchParams,
  setSearchParams,
  searchComponent,
}: ExcludedLeadsProps) {
  const isDesktop = useTwBreakpoint("lg");

  //the exclusions-search returns plain profiles, so does the selection
  const [selectedLeads, setSelectedLeads] = useState<LinkedInProfile[]>([]);

  const { page, setPage, pageSize, setPageSize } =
    usePagination("lead-list-leads");
  const { resetParams } = useListLeadsParams(searchParams, setSearchParams);

  const {
    excludedLeads,
    excludedLeadsTotal,
    isFetchingExcludedLeads,
    excludedLeadsError,
    refetchExcludedLeads,
  } = useExcludedListLeads({
    listId,
    searchParams,
    pagingParams: { page, pageSize },
  });

  //any change to the search invalidates the current page, so does opening the tab
  useEffect(() => {
    setPage(0);
  }, [searchParams]);

  //the selection only covers the rows of the page that's listed
  useEffect(() => {
    setSelectedLeads([]);
  }, [searchParams, page]);

  if (excludedLeadsError) {
    return (
      <ErrorPlaceholder
        errorMessage="Error loading excluded"
        onRetry={refetchExcludedLeads}
      />
    );
  }

  const hasNoLeads = !isFetchingExcludedLeads && !excludedLeads?.length;

  //there's nothing to search through, so only the placeholder is shown
  if (hasNoLeads && !searchParams.keyword) {
    return <NoLeadsPlaceholder type="excluded" />;
  }

  const totalItemCount = excludedLeadsTotal || 0;

  const tableProps = {
    leads: excludedLeads,
    isLoading: isFetchingExcludedLeads,
    pageSize,
    totalItemCount,
    selectedLeads,
    setSelectedLeads,
  };

  const content = hasNoLeads ? (
    <NoResultsPlaceholder onClear={resetParams} />
  ) : (
    <>
      {isDesktop ? (
        <DesktopTable {...tableProps} listId={listId} />
      ) : (
        <MobileList {...tableProps} />
      )}

      <PaginationFooter
        pageSize={pageSize}
        setPageSize={setPageSize}
        selectedPage={page}
        setSelectedPage={setPage}
        totalItemCount={totalItemCount}
        isLastPageHidden={false}
      />
    </>
  );

  return (
    <>
      {searchComponent}

      {/* on desktop the bulk actions live in the table's own selection row */}
      <RenderIf condition={!isDesktop && !!selectedLeads.length}>
        <ManageListLeads
          listId={listId}
          isExcluded
          selectedLeads={selectedLeads}
        />
      </RenderIf>

      {content}
    </>
  );
}
